import React, { useState } from 'react';
import {
  Button,
  Descriptions,
  Space,
  Typography,
  Divider,
  Modal,
  message,
} from 'antd';
import { CopyOutlined, EyeOutlined } from '@ant-design/icons';
import type { AgentDatosExtraidos } from '../../types/agent';
import { registerActivity } from '../../api/agentApi';

const { Title, Text } = Typography;

interface AgentSummaryProps {
  clienteId?: number;
  datosExtraidos: AgentDatosExtraidos;
  onConfirmed: () => void;
  onBack: () => void;
}

/** Filtra las entradas de un objeto, excluyendo valores nulos, undefined, arrays vacíos y objetos vacíos */
function definedEntries(obj: Record<string, unknown>): [string, unknown][] {
  return Object.entries(obj).filter(([, v]) => {
    if (v === null || v === undefined) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v as object).length > 0;
    return true;
  });
}

/** Convierte snake_case a etiqueta legible */
function toLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Formatea un valor para mostrarlo como string */
function formatValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'object' && value !== null) {
    return definedEntries(value as Record<string, unknown>)
      .map(([k, v]) => `${toLabel(k)}: ${formatValue(v)}`)
      .join(' · ');
  }
  return String(value);
}

interface SectionProps {
  title: string;
  data: Record<string, unknown>;
  exclude?: string[];
}

const SummarySection: React.FC<SectionProps> = ({ title, data, exclude = [] }) => {
  const entries = definedEntries(data).filter(([k]) => !exclude.includes(k));
  if (entries.length === 0) return null;

  return (
    <>
      <Title level={5} style={{ marginBottom: 8, marginTop: 16 }}>
        {title}
      </Title>
      <Descriptions
        size="small"
        column={1}
        bordered
        labelStyle={{ width: 200, fontWeight: 500 }}
      >
        {entries.map(([key, value]) => (
          <Descriptions.Item key={key} label={toLabel(key)}>
            {formatValue(value)}
          </Descriptions.Item>
        ))}
      </Descriptions>
    </>
  );
};

const AgentSummary: React.FC<AgentSummaryProps> = ({
  clienteId,
  datosExtraidos,
  onConfirmed,
  onBack,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { actividad, cliente, farmacia, proxima_accion } = datosExtraidos;

  // Construir el JSON completo para exportar
  const jsonData = {
    clienteId,
    actividad,
    cliente,
    farmacia,
    proxima_accion: proxima_accion.omitir ? null : proxima_accion,
  };

  const jsonString = JSON.stringify(jsonData, null, 2);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      message.success('JSON copiado al portapapeles');
    } catch {
      message.error('No se pudo copiar al portapapeles');
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await registerActivity({
        clienteId,
        datos: datosExtraidos,
      });
      message.success('Actividad registrada correctamente');
      onConfirmed();
    } catch (err) {
      console.error('Error al registrar actividad:', err);
      message.error('Error al registrar la actividad');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          Resumen de la actividad
        </Title>
        <Text type="secondary">
          Revisa los datos extraídos antes de confirmar.
        </Text>
      </div>

      {clienteId && (
        <Text>
          <strong>Cliente ID:</strong> {clienteId}
        </Text>
      )}

      <SummarySection
        title="Actividad"
        data={actividad as unknown as Record<string, unknown>}
        exclude={['origen']}
      />

      <SummarySection
        title="Cliente"
        data={cliente as unknown as Record<string, unknown>}
      />

      <SummarySection
        title="Farmacia"
        data={farmacia as unknown as Record<string, unknown>}
      />

      {!proxima_accion.omitir && (
        <SummarySection
          title="Próxima acción"
          data={proxima_accion as unknown as Record<string, unknown>}
          exclude={['omitir']}
        />
      )}

      {proxima_accion.omitir && (
        <Text type="secondary" style={{ fontStyle: 'italic' }}>
          Sin próxima acción programada.
        </Text>
      )}

      <Divider />

      <Space>
        <Button onClick={onBack}>
          Volver al chat
        </Button>
        <Button type="primary" onClick={handleConfirm} loading={submitting}>
          Confirmar
        </Button>
      </Space>

      <Divider />

      <Space align="center">
        <Text strong>Actividad y datos recogidos</Text>
        <Button
          icon={<EyeOutlined />}
          onClick={() => setModalVisible(true)}
        >
          Ver
        </Button>
      </Space>

      <Modal
        title="JSON de datos extraídos"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={700}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={handleCopyToClipboard}>
            Copiar al portapapeles
          </Button>,
          <Button key="close" onClick={() => setModalVisible(false)}>
            Cerrar
          </Button>,
        ]}
      >
        <pre
          style={{
            backgroundColor: '#f5f5f5',
            padding: 16,
            borderRadius: 8,
            maxHeight: 400,
            overflow: 'auto',
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {jsonString}
        </pre>
      </Modal>
    </Space>
  );
};

export default AgentSummary;
